/**
 * OfflineBanner — Visual indicator for offline state + pending sync queue
 * 
 * Sticky banner at top of screen when offline or when sync items are pending.
 * Transparent when online + no pending items.
 */

import React from 'react';

export default function OfflineBanner({ isOffline, pendingCount, isSyncing, lastSync, onRetry }) {
    // Fully online, no pending items → show nothing
    if (!isOffline && pendingCount === 0 && !isSyncing) return null;

    const formatTime = (iso) => {
        if (!iso) return '–';
        try {
            return new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
        } catch {
            return '–';
        }
    };

    // Syncing state (green pulse)
    if (isSyncing) {
        return (
            <div className="fixed top-0 left-0 right-0 z-[9999] bg-blue-900/95 border-b-2 border-blue-400 px-4 py-2 flex items-center justify-center gap-3 backdrop-blur-sm">
                <span className="w-3 h-3 rounded-full bg-blue-400 animate-ping" />
                <span className="text-blue-100 text-xs font-bold">Synchroniseren...</span>
                {pendingCount > 0 && (
                    <span className="text-blue-300 text-xs">({pendingCount} items)</span>
                )}
            </div>
        );
    }

    // Fully offline
    if (isOffline) {
        return (
            <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-900/95 border-b-2 border-amber-500 px-4 py-2 flex items-center justify-between backdrop-blur-sm">
                <div className="flex items-center gap-3">
                    <span className="text-lg">📡</span>
                    <div>
                        <span className="text-amber-100 text-xs font-bold block">Offline modus</span>
                        <span className="text-amber-300 text-[10px]">
                            Wijzigingen worden opgeslagen en gesynchroniseerd bij verbinding
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {pendingCount > 0 && (
                        <span className="bg-amber-700 text-amber-100 text-[10px] px-2 py-1 rounded-full font-bold">
                            {pendingCount} wachtend
                        </span>
                    )}
                    {lastSync && (
                        <span className="text-amber-400 text-[10px]">
                            Laatst gesync: {formatTime(lastSync)}
                        </span>
                    )}
                </div>
            </div>
        );
    }

    // Online but has pending items (shouldn't happen normally, but just in case)
    if (pendingCount > 0) {
        return (
            <div className="fixed top-0 left-0 right-0 z-[9999] bg-yellow-900/95 border-b-2 border-yellow-500 px-4 py-2 flex items-center justify-between backdrop-blur-sm">
                <div className="flex items-center gap-3">
                    <span className="text-lg">⏳</span>
                    <span className="text-yellow-100 text-xs font-bold">{pendingCount} wachtende wijzigingen</span>
                </div>
                {onRetry && (
                    <button
                        onClick={onRetry}
                        className="text-[10px] font-bold text-yellow-900 bg-yellow-400 hover:bg-yellow-300 px-3 py-1 rounded-full"
                    >
                        Nu synchroniseren
                    </button>
                )}
            </div>
        );
    }

    return null;
}
